import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getCurrencyConfig, widgetConfig } from '../../config/config';
import { Currency } from '../../types/currency';
import { LoanType } from '../../types/loanType';
import { getSummaryData } from '../../utils/getSummaryData';
import styles from './EmailSection.module.less';

interface EmailSectionProps {
  loanType: LoanType;
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  currency: Currency;
  amount: string;
  period: number;
  customInterestRate: string;
  error?: string;
  errorKey?: number;
}

const getLoanTypeKey = (loanType: LoanType): string => {
  switch (loanType) {
    case LoanType.Instant:
      return 'instant';
    case LoanType.Custom:
      return 'custom';
    case LoanType.Otc:
      return 'otc';
    default:
      return 'instant';
  }
};

export const EmailSection: React.FC<EmailSectionProps> = ({
  loanType,
  email,
  onEmailChange,
  onSubmit,
  currency,
  amount,
  period,
  customInterestRate,
  error,
  errorKey = 0,
}) => {
  const intl = useIntl();

  const formatCurrency = (value: string, cur: Currency): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return `${cur} ${intl.formatNumber(Math.round(num), { maximumFractionDigits: 0 })}`;
  };

  const formatBitcoin = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return `${num.toFixed(5)} BTC`;
  };

  const loanTypeKey = getLoanTypeKey(loanType);

  const showError = !!error;

  const summaryData = useMemo(() => {
    if (loanType === LoanType.Otc) {
      return null;
    }

    const currencyConfig = getCurrencyConfig(currency);
    if (!currencyConfig) return null;

    const ticket = currencyConfig.investTickets.find(t => t.period === period);

    return getSummaryData({
      amount,
      period,
      currency,
      loanType,
      customInterestRate,
      instantRate: ticket?.interestRate,
      originationFeeRate: widgetConfig.originationFeeRate,
      bitcoinPrice: currencyConfig.bitcoinPrice,
    });
  }, [loanType, currency, amount, period, customInterestRate]);

  return (
    <>
      <div className={styles.emailContentContainer}>
        <div className={styles.headerSection}>
          <h2 className={styles.header}>
            {intl.formatMessage({ id: `email.header.${loanTypeKey}` })}
          </h2>
        </div>

        <div className={styles.subtextContainer}>
          <p className={styles.subtext}>
            {intl.formatMessage({ id: `email.subtext.${loanTypeKey}` })}
          </p>
          <p className={styles.subtextHighlight}>
            {intl.formatMessage({ id: `email.subtextHighlight.${loanTypeKey}` })}
          </p>
        </div>

        <div className={styles.inputSection}>
          <div className={styles.inputWrapper}>
            <label className={styles.inputLabel}>
              {intl.formatMessage({ id: 'email.label' })}
            </label>
            <input
              type="email"
              className={`${styles.emailInput} ${showError ? styles.emailInputError : ''}`}
              value={email}
              onInput={e => onEmailChange((e.target as HTMLInputElement).value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              placeholder={intl.formatMessage({ id: 'email.placeholder' })}
            />
            {showError && (
              <span key={errorKey} className={styles.errorMessage}>
                {error}
              </span>
            )}
          </div>
        </div>

        {summaryData && (
          <div className={styles.summarySection}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>
                {intl.formatMessage(
                  { id: 'email.summary.totalRepayment' },
                  { period }
                )}
              </span>
              <span className={styles.summaryValue}>
                {formatCurrency(summaryData.amountDue, currency)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>
                {intl.formatMessage({ id: 'email.summary.collateralNeeded' })}
              </span>
              <span className={styles.summaryValue}>
                ≈ {formatBitcoin(summaryData.btcTotal)}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
